import type { ChainId } from "../../server/types";

export const CHAIN_LABELS: Record<ChainId, string> = {
  solana: "Solana",
  ethereum: "Ethereum",
  bsc: "BNB Chain",
  robinhood: "Robinhood",
};

export const CHAIN_SHORT: Record<ChainId, string> = {
  solana: "SOL",
  ethereum: "ETH",
  bsc: "BSC",
  robinhood: "RHC",
};

export function compactNumber(value?: number, maximumFractionDigits = 2): string {
  if (value === undefined || value === null || !Number.isFinite(value)) return "—";
  return new Intl.NumberFormat("en-US", {
    notation: Math.abs(value) >= 1000 ? "compact" : "standard",
    maximumFractionDigits,
  }).format(value);
}

export function currency(value?: number): string {
  if (value === undefined || value === null || !Number.isFinite(value)) return "—";
  if (value > 0 && value < 0.0001) return `$${value.toExponential(2)}`;
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    notation: value >= 1_000_000 ? "compact" : "standard",
    maximumFractionDigits: value < 1 ? 6 : 2,
  }).format(value);
}

export function shortAddress(address: string, size = 5): string {
  if (address.length <= size * 2 + 3) return address;
  return `${address.slice(0, size)}…${address.slice(-size)}`;
}

export function relativeTime(iso?: string): string {
  if (!iso) return "Unknown";
  const delta = Date.now() - new Date(iso).getTime();
  const minutes = Math.floor(delta / 60_000);
  if (minutes < 1) return "just now";
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
}

export function explorerAddress(chain: ChainId, address: string): string {
  if (chain === "solana") return `https://solscan.io/account/${address}`;
  if (chain === "ethereum") return `https://etherscan.io/address/${address}`;
  if (chain === "bsc") return `https://bscscan.com/address/${address}`;
  return `https://robinhoodchain.blockscout.com/address/${address}`;
}

export function explorerTransaction(chain: ChainId, hash: string): string {
  if (chain === "solana") return `https://solscan.io/tx/${hash}`;
  if (chain === "ethereum") return `https://etherscan.io/tx/${hash}`;
  if (chain === "bsc") return `https://bscscan.com/tx/${hash}`;
  return `https://robinhoodchain.blockscout.com/tx/${hash}`;
}

/**
 * Continuous colour for the suspicion score.
 *
 * Four fixed band colours made the scale feel like a cliff: 21 was green and 22
 * was yellow, which overstates the difference between two nearly identical
 * readings. This interpolates green through lime, yellow, and orange to red, and
 * quantises to twenty steps so the result still reads as a graded scale rather
 * than an imperceptible smear.
 */
const RISK_STOPS: Array<[number, [number, number, number]]> = [
  [0, [34, 197, 94]],
  [25, [132, 204, 22]],
  [45, [234, 179, 8]],
  [65, [249, 115, 22]],
  [85, [239, 68, 68]],
  [100, [190, 18, 60]],
];

export function riskColor(score: number): string {
  const clamped = Math.min(100, Math.max(0, Number.isFinite(score) ? score : 0));
  // Twenty buckets across the range.
  const stepped = Math.round(clamped / 5) * 5;
  let lower = RISK_STOPS[0];
  let upper = RISK_STOPS[RISK_STOPS.length - 1];
  for (let index = 0; index < RISK_STOPS.length - 1; index += 1) {
    if (stepped >= RISK_STOPS[index][0] && stepped <= RISK_STOPS[index + 1][0]) {
      lower = RISK_STOPS[index];
      upper = RISK_STOPS[index + 1];
      break;
    }
  }
  const span = upper[0] - lower[0] || 1;
  const ratio = (stepped - lower[0]) / span;
  const channel = (index: number) =>
    Math.round(lower[1][index] + (upper[1][index] - lower[1][index]) * ratio);
  return `rgb(${channel(0)}, ${channel(1)}, ${channel(2)})`;
}

/** The full sweep, for rendering the scale the score sits on. */
export function riskGradient(): string {
  return `linear-gradient(90deg, ${RISK_STOPS.map(([at, rgb]) => `rgb(${rgb.join(",")}) ${at}%`).join(", ")})`;
}
